import React, { useMemo, useCallback, useState } from 'react';
import { Info, ChevronDown } from 'lucide-react';
import InfoTooltip from './InfoTooltip';
import {
    TIMELINE_BUCKETS,
    getBucketForAction,
    getActionType,
    getProceduralOrder
} from '../constants/legislativeActions';

const BillTimeline = ({ bill, isDarkMode }) => {
    const [expandedStep, setExpandedStep] = useState(null);

    // Filter out duplicate identical actions on the same date (data hygiene)
    const normalizedHistory = useMemo(() => {
        if (!bill?.history) return [];
        const seen = new Set();
        return bill.history.filter(h => {
            const key = `${h.date}|${h.action}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [bill?.history]);

    if (!bill) return null;

    const formatStepDate = (dateString, fallback = '') => {
        if (!dateString) return fallback;
        try {
            // Parse date as local date to avoid timezone issues
            let date;
            if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
                const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
                date = new Date(year, month - 1, day);
            } else {
                date = new Date(dateString);
            }
            if (isNaN(date.getTime())) return fallback;
            return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
        } catch (e) {
            return dateString;
        }
    };

    // Sort History Chronologically
    const sortedHistory = useMemo(() => {
        return [...normalizedHistory].sort((a, b) => {
            const dateCompare = new Date(a.date) - new Date(b.date);
            if (dateCompare !== 0) return dateCompare;
            // If same date, sort by procedural order
            return getProceduralOrder(a.action) - getProceduralOrder(b.action);
        });
    }, [normalizedHistory]);

    // Define Steps Logic with Bill/Resolution Pathing
    const getSteps = useCallback(() => {
        const billNum = (bill.bill_number || '').toUpperCase();
        // Determine Origin
        const isSenateOrigin = billNum.startsWith('S');
        const originChamber = isSenateOrigin ? 'Senate' : 'House';
        const secondChamber = isSenateOrigin ? 'House' : 'Senate';

        // Determine Bill Type & Path
        const isResolution = billNum.includes('R') && !billNum.includes('JR'); // Simple Resolution
        const isJointResolution = billNum.includes('JR');
        const isBill = billNum.startsWith('HB') || billNum.startsWith('SB') || isJointResolution;

        // --- DEFINE TIMELINE PATH ---
        // Bills/Joint Res: Full 7-stage path
        // Simple Res: 2-stage path (Intro -> Floor/Adopted, no committee)
        let pathKeys = ['intro'];

        if (isResolution) {
            // Simple resolutions: Intro -> Floor (no committee step)
            pathKeys.push('floor1');
        } else if (isBill) {
            // Bills and Joint Resolutions: Full path with committees
            pathKeys.push('comm1', 'floor1', 'comm2', 'floor2', 'recon', 'gov');
        } else {
            // Fallback: include comm1 and floor1 for unknown types
            pathKeys.push('comm1', 'floor1');
        }

        // Initialize Buckets
        const buckets = {};
        pathKeys.forEach(key => buckets[key] = []);

        // --- POPULATE BUCKETS ---
        // Use unified getBucketForAction helper
        sortedHistory.forEach(h => {
            const bucketKey = getBucketForAction({ code: h.code, action: h.action }, originChamber);

            // If the bucket exists in our path, add it
            if (buckets[bucketKey]) {
                buckets[bucketKey].push(h);
            } else if (bucketKey === 'gov' && !isBill) {
                // Edge case: A simple resolution shouldn't go to Gov, but if data says so, show it?
                // For now, strict pathing: ignore or map to floor1 (adopted/signed mixup?)
                // But usually resolutions stop at floor1.
            }
        });

        // --- CONSTRUCT STEPS CONFIG ---
        const stepsConfig = pathKeys.map(key => {
            const def = TIMELINE_BUCKETS[key];
            const isOrigin = key.includes('1') || key === 'intro';
            const chamberCtx = isOrigin ? originChamber : secondChamber;

            // Customize Title dynamically
            let title = typeof def.title === 'function' ? def.title(chamberCtx) : def.title;

            // For simple resolutions, "Floor Vote" -> "Adoption Vote" maybe?
            // "House Floor Vote" is fine.

            return {
                id: def.id,
                title: title,
                bucket: buckets[key],
                completion: def.completion || [],
                failure: def.failure || [], // e.g. ['veto', 'lost']
                tooltip: typeof def.tooltip === 'function' ? def.tooltip(chamberCtx) : def.tooltip,
                stage: def.stage
            };
        });

        // Remove 'recon' if empty (Conditional Step)
        const finalStepList = stepsConfig.filter(s => {
            if (s.id === 'recon' && s.bucket.length === 0) return false;
            return true;
        });

        // --- COMPUTE STEP STATUSES ---
        let isDead = false;

        // 1. Identify the Current Active Stage based on the LATEST action
        // This handles "backward" movement (e.g. Floor -> Recommitted -> Committee)
        let activeStepIndex = 0; // Default to Intro

        if (sortedHistory.length > 0) {
            const latestAction = sortedHistory[sortedHistory.length - 1];
            // Determine which bucket/step this latest action belongs to
            const latestBucketId = getBucketForAction({ code: latestAction.code, action: latestAction.action }, originChamber);

            // Find index of this bucket in our steps list
            const idx = finalStepList.findIndex(s => s.id === latestBucketId);
            if (idx !== -1) {
                activeStepIndex = idx;
            } else {
                // Edge case: Action belongs to a bucket not in current path
                // Default to last available step or keep at 0 if confused
                activeStepIndex = finalStepList.length - 1;
            }

            // Check if DEAD based on latest action (Terminal Failure)
            const latestType = getActionType(latestAction.action);
            if (latestType === 'negative') {
                isDead = true;
            }
        }

        // Determine if there is a Final Outcome (Passed/Signed/Dead vs In Progress)
        let isFinalOutcome = isDead;
        if (!isFinalOutcome && sortedHistory.length > 0) {
            const latestAction = sortedHistory[sortedHistory.length - 1];
            // If we are at the last step and the action was positive (Passed/Signed/Adopted), it's final.
            if (activeStepIndex === finalStepList.length - 1) {
                const latestType = getActionType(latestAction.action);
                if (latestType === 'positive') {
                    isFinalOutcome = true;
                }
            }
        }

        // 2. Map Statuses based on Active Index
        const finalSteps = finalStepList.map((s, i) => {
            const hasItems = s.bucket.length > 0;
            const lastItem = hasItems ? s.bucket[s.bucket.length - 1] : null;

            // Description Logic
            let description = 'Pending';
            let displayDate = '';
            if (hasItems && lastItem) {
                description = lastItem.action;
                displayDate = formatStepDate(lastItem.date);
            }

            // --- STATUS ASSIGNMENT ---
            let status = 'upcoming'; // Default

            if (isDead) {
                // If Bill is Dead/Failed
                if (i === activeStepIndex) {
                    status = 'failed';
                } else if (i < activeStepIndex) {
                    status = 'completed';
                } else {
                    status = 'upcoming'; // Grey
                }
            } else {
                if (i < activeStepIndex) {
                    // Past steps: Completed
                    status = 'completed';

                    // If no history items in a past step AND the bill is finalized, it was Skipped
                    // Otherwise (in progress), treat as default (Completed/Pending)
                    if (!hasItems && isFinalOutcome) {
                        status = 'skipped';
                        displayDate = 'Skipped';
                        description = '';
                    }
                } else if (i === activeStepIndex) {
                    // Current Active Step
                    if (s.bucket.length > 0) {
                        const latestAction = s.bucket[s.bucket.length - 1]; // chronological last in this bucket
                        const type = getActionType(latestAction.action);

                        // Check for explicit completion of THIS step
                        const completionKeywords = s.completion || [];
                        const isExplicitComplete = type === 'positive' && completionKeywords.some(k => latestAction.action.toLowerCase().includes(k));

                        if (isExplicitComplete) {
                            status = 'completed';
                        } else if (type === 'procedural') {
                            // Recommitted, Tabled, etc.
                            status = 'stalled'; // Amber
                        } else {
                            status = 'current'; // Green Pulse
                        }

                        // Override: If final step in whole timeline is Reached & Positive, it's completed
                        if (i === finalStepList.length - 1 && type === 'positive') {
                            status = 'completed';
                        }

                    } else {
                        status = 'current'; // Should have items if it's activeIndex, but robust fallback
                    }

                } else {
                    // Future Steps (i > activeStepIndex) -> Reset to Upcoming
                    status = 'upcoming';
                }
            }

            return {
                ...s,
                status,
                description,
                displayDate,
                history: s.bucket
            };
        });

        // 3. Final cleanup
        return finalSteps.map(s => ({
            ...s,
            isCurrent: s.status === 'current' || s.status === 'stalled',
        }));

    }, [bill, sortedHistory]);

    const steps = useMemo(() => getSteps(), [getSteps]);

    return (
        <div className={`w-full max-w-[600px] mx-auto py-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            <div className="relative pl-0">
                {/* Continuous Spine Line */}
                <div className={`absolute top-2 bottom-4 left-8 w-[2px] -ml-[1px] ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>

                <div className="flex flex-col gap-8">
                    {steps.map((step, index) => {
                        // Visual Styles
                        let dotClass = '';
                        let ringClass = '';
                        let pulseClass = '';
                        let statusLabel = '';
                        let labelColor = '';

                        // Determine Color based on status
                        if (step.status === 'failed') {
                            dotClass = 'bg-red-500 border-red-500';
                            ringClass = 'bg-red-400';
                            statusLabel = 'Failed';
                            labelColor = isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700';
                        } else if (step.status === 'stalled') {
                            dotClass = 'bg-amber-500 border-amber-500';
                            ringClass = 'bg-amber-400';
                            statusLabel = 'Recommitted';
                            labelColor = isDarkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-500';
                        } else if (step.status === 'skipped') {
                            dotClass = isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-300 border-gray-400';
                            statusLabel = ''; // No pill for skipped
                        } else if (step.status === 'completed') {
                            // Finished -> Static Green
                            dotClass = 'bg-green-500 border-green-500';
                        } else if (step.status === 'current') {
                            // Active Step -> Blue Pulsing
                            dotClass = 'bg-blue-600 border-blue-600';
                            ringClass = 'bg-blue-400';
                            pulseClass = 'animate-soft-pulse';
                            statusLabel = 'Current';
                            labelColor = isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700';
                        } else {
                            // Upcoming
                            dotClass = isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300';
                        }

                        return (
                            <div key={step.id || index} className="relative flex items-start group">
                                {/* Dot & Pulse Group */}
                                <div className="absolute left-8 -translate-x-1/2 mt-[7px] w-3 h-3 flex items-center justify-center z-10">
                                    {(step.status === 'current' || step.status === 'failed' || step.status === 'stalled') && (
                                        <div className={`absolute w-full h-full rounded-full ${ringClass} ${pulseClass}`}></div>
                                    )}
                                    <div className={`absolute w-full h-full rounded-full border-2 box-content ${dotClass}`}></div>
                                </div>

                                {/* Content */}
                                <div className="ml-16 w-full pr-4">
                                    <div className={`text-xs mb-1 font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {step.displayDate || <span className="invisible">Pending</span>}
                                    </div>

                                    <div className="flex items-center flex-wrap gap-2">
                                        <h3 className={`font-bold leading-tight ${step.status === 'upcoming'
                                            ? (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                                            : (isDarkMode ? 'text-gray-100' : 'text-gray-900')}`}>
                                            {step.title}
                                        </h3>

                                        {statusLabel && (
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${labelColor}`}>
                                                {statusLabel}
                                            </span>
                                        )}
                                    </div>

                                    {/* Description (Hidden if skipped) */}
                                    {step.status !== 'skipped' && (
                                        <div
                                            onClick={() => (step.history.length > 0 ? setExpandedStep(expandedStep === index ? null : index) : null)}
                                            className={`mt-1 select-none group/step ${step.history.length > 0 ? 'cursor-pointer' : ''}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className={`text-sm leading-snug transition-colors ${step.status === 'upcoming'
                                                    ? (isDarkMode ? 'text-gray-600' : 'text-gray-400')
                                                    : (isDarkMode ? 'text-gray-300' : 'text-gray-600')
                                                    } ${step.history.length > 0 ? 'group-hover/step:text-blue-500' : ''}`}>
                                                    {step.description}
                                                </div>

                                                <InfoTooltip
                                                    content={step.tooltip}
                                                    isDarkMode={isDarkMode}
                                                    size="sm"
                                                    underline={false}
                                                >
                                                    <Info className="w-3.5 h-3.5 text-gray-400 opacity-50 hover:opacity-100" />
                                                </InfoTooltip>

                                                {step.history.length > 1 && (
                                                    <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${expandedStep === index ? 'rotate-180' : ''}`} />
                                                )}
                                            </div>

                                            {/* Drill-down history entries */}
                                            {expandedStep === index && step.history.length > 0 && (
                                                <div className={`mt-2 py-2 px-3 rounded-lg border animate-in fade-in slide-in-from-top-1 duration-150 ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                                                    }`}>
                                                    <div className="space-y-2">
                                                        {step.history.map((h, hIdx) => {
                                                            const hType = getActionType(h.action);
                                                            // Visual mapping from type to color
                                                            const hColor = hType === 'negative' ? 'text-red-500' : (
                                                                hType === 'procedural' ? 'text-amber-500' : (
                                                                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                                                                )
                                                            );

                                                            return (
                                                                <div key={hIdx} className="flex gap-3 text-xs">
                                                                    <span className={`font-mono flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                                                        {formatStepDate(h.date)}
                                                                    </span>
                                                                    <span className={hColor}>
                                                                        {h.action}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default BillTimeline;
